import styled from 'styled-components';
import { Trans } from '@lingui/react';
import RegisterFormTemplate from '@ecosystems/registerForm.pageTemplate';
import { H2 } from '@components/typography/Headings';
import Trailer from '@organisms/video-player/Trailer';
import { RegistrationSourceKind, Video } from '@gql/generated';
import { Separator } from 'src/ui/separator';
import { useMemo } from 'react';

const LeftContainer = styled(RegisterFormTemplate.LeftContainer)`
  display: flex;
  justify-content: center;
  background: #667c91;

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    height: 100vh;
    min-height: 100vh;
  }
`;

export const Title = styled(H2)`
  max-width: 80%;
  color: ${({ theme }) => theme.colors.neutrals.black};
  font-size: 28px;
  line-height: 34px;
  font-weight: bold;
  margin-top: 30px;
  margin-bottom: 10px;

  @media (min-width: ${({ theme }) => theme.viewports.laptop}px) {
    margin-top: 40px;
  }
`;

const heroVideo = {
  id: 'be61f6ad-357e-4017-9ee1-b886cf615c53',
  identifier: '28dx',
  title: 'Trailer_registration flow',
  hls: 'https://yogobe.23video.com/64968566/111050676/46946bcfb9e03db3ca09d7420813e44d/video_hls/playlist.m3u8',
  assets: {
    thumbnailUrl:
      'https://yogobe.23video.com/64968566/111050676/bd3787de780c78af308dbf2126a56da9/large/embed/thumbnail.jpg',
  },
  meta: {
    minutes: 3,
  },
  player: {
    assets: [
      {
        src: 'https://yogobe.23video.com/64968566/111050676/46946bcfb9e03db3ca09d7420813e44d/video_mobile_high/regvideo-trailerfinal-10-video.mp4',
        resolution: 180,
        size: 1133322,
      },
      {
        src: 'https://yogobe.23video.com/64968566/111050676/46946bcfb9e03db3ca09d7420813e44d/video_medium/regvideo-trailerfinal-10-video.mp4',
        resolution: 360,
        size: 2029012,
      },
      {
        src: 'https://yogobe.23video.com/64968566/111050676/46946bcfb9e03db3ca09d7420813e44d/video_hd/regvideo-trailerfinal-10-video.mp4',
        resolution: 720,
        size: 4077635,
      },
      {
        src: 'https://yogobe.23video.com/64968566/111050676/46946bcfb9e03db3ca09d7420813e44d/video_1080p/regvideo-trailerfinal-10-video.mp4',
        resolution: 1080,
        size: 9555724,
      },
    ],
  },
} as Video;

const Position = ({ children }) => {
  return (
    <span className="flex-shrink-0 inline-flex justify-center items-center size-8 bg-white/20 rounded-full">
      {children}
    </span>
  );
};

const RegisterLeftContent = ({
  registrationSource,
  imageUrl,
}: {
  registrationSource: RegistrationSourceKind;
  imageUrl: string;
}) => {
  const text = useMemo(() => {
    if (
      [RegistrationSourceKind.Live, RegistrationSourceKind.PaidLive].includes(
        registrationSource
      )
    ) {
      return {
        imageUrl,
        title: <Trans id="register_v2.live_right_side.title" />,
        item1: <Trans id="register_v2.live_right_side.item_1" />,
        item2: <Trans id="register_v2.live_right_side.item_2" />,
        item3: <Trans id="register_v2.live_right_side.item_3" />,
      };
    } else if (RegistrationSourceKind.Course === registrationSource) {
      return {
        imageUrl,
        title: <Trans id="register_v2.course_right_side.title" />,
        item1: <Trans id="register_v2.course_right_side.item_1" />,
        item2: <Trans id="register_v2.course_right_side.item_2" />,
        item3: <Trans id="register_v2.course_right_side.item_3" />,
      };
    } else if (RegistrationSourceKind.ContentHubBundle === registrationSource) {
      return {
        title: <Trans id="register_v2.content_hub_right_side.title" />,
        item1: <Trans id="register_v2.content_hub_right_side.item_1" />,
        item2: <Trans id="register_v2.content_hub_right_side.item_2" />,
        item3: <Trans id="register_v2.content_hub_right_side.item_3" />,
      };
    } else if (RegistrationSourceKind.Wellness === registrationSource) {
      return {
        title: <Trans id="register_v2.wellness_right_side.title" />,
        item1: <Trans id="register_v2.wellness_right_side.item_1" />,
        item2: <Trans id="register_v2.wellness_right_side.item_2" />,
        item3: <Trans id="register_v2.wellness_right_side.item_3" />,
      };
    } else if (
      [
        RegistrationSourceKind.B2hFar,
        RegistrationSourceKind.B2hProgram,
      ].includes(registrationSource)
    ) {
      return {
        imageUrl,
        title: <Trans id="register_v2.b2h_right_side.title" />,
        item1: <Trans id="register_v2.b2h_right_side.item_1" />,
        item2: <Trans id="register_v2.b2h_right_side.item_2" />,
        item3: <Trans id="register_v2.b2h_right_side.item_3" />,
      };
    } else {
      return {
        title: <Trans id="register_v2.play_right_side.title" />,
        item1: <Trans id="register_v2.play_right_side.item_1" />,
        item2: <Trans id="register_v2.play_right_side.item_2" />,
        item3: <Trans id="register_v2.play_right_side.item_3" />,
      };
    }
  }, [registrationSource, imageUrl]);

  return (
    <LeftContainer>
      <div
        className={
          'max-w-lg w-full mx-auto flex flex-col justify-center items-center px-5 py-12 ' +
          (!registrationSource ? 'hidden' : '')
        }
      >
        <div className="w-full aspect-video rounded-lg overflow-hidden">
          {text.hasOwnProperty('imageUrl') ? (
            <img
              className="size-full object-cover bg-slate-50/30"
              src={text.imageUrl}
            />
          ) : (
            <Trailer
              elementID="trailer"
              video={heroVideo}
              thumbnailImage={heroVideo.assets.thumbnailUrl}
              isLimited={false}
              autoPlay={false}
            />
          )}
        </div>
        <div className="text-white py-10">
          <p className="text-xl font-medium m-0 pb-6">{text.title}</p>
          <ul className="flex flex-col gap-5 text-base font-medium m-0 list-none p-0 [&_li]:flex [&_li]:items-baseline [&_li]:gap-2">
            <li>
              <Position>1</Position>
              {text.item1}
            </li>
            <li>
              <Position>2</Position>
              {text.item2}
            </li>
            <li>
              <Position>3</Position>
              {text.item3}
            </li>
          </ul>
        </div>
        <Separator className="bg-white/10" />
        <div className="w-full flex items-center gap-2 pt-10 pb-5">
          <img className="h-10" src="/static/icons/avatars.webp" />
          <span className="text-white font-medium">
            <Trans id="register_v2.right_side.members_count" />
          </span>
        </div>
      </div>
    </LeftContainer>
  );
};

export default RegisterLeftContent;
